import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Clock, DollarSign, Users, ArrowRight } from 'lucide-react';
import Button from '@/components/ui/Button';

interface ROIInputs {
  employeesCount: number;
  hourlyRate: number;
  hoursPerWeek: number;
  weeksPerYear: number;
}

const ROICalculator = () => {
  const [inputs, setInputs] = useState<ROIInputs>({
    employeesCount: 5,
    hourlyRate: 15,
    hoursPerWeek: 10,
    weeksPerYear: 48,
  });

  const [savings, setSavings] = useState({
    annual: 0,
    fiveYear: 0,
    timeRecovered: 0,
  });

  useEffect(() => {
    const annualHours = inputs.hoursPerWeek * inputs.weeksPerYear;
    const annualCost = annualHours * inputs.hourlyRate * inputs.employeesCount;
    const automationSavingsRate = 0.7; // 70% reduction in time
    
    const annualSavings = annualCost * automationSavingsRate;
    const fiveYearSavings = annualSavings * 5;
    const timeRecovered = annualHours * automationSavingsRate;

    setSavings({
      annual: Math.round(annualSavings),
      fiveYear: Math.round(fiveYearSavings),
      timeRecovered: Math.round(timeRecovered),
    });
  }, [inputs]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0,
    }));
  };

  return (
    <section id="calculator" className="py-20 lg:py-24 bg-white">
      <div className="container px-4 mx-auto">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                Calculez vos <span className="text-orcsys-500">économies</span>
              </h2>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                Estimez rapidement le retour sur investissement de l'automatisation 
                de vos processus métier.
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Calculator Inputs */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-6 shadow-lg"
            >
              <h3 className="text-xl font-semibold text-slate-900 mb-6">
                Informations sur vos processus actuels
              </h3>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nombre d'employés impliqués
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                      <Users className="w-5 h-5 text-slate-400" />
                    </div>
                    <input
                      type="number"
                      name="employeesCount"
                      value={inputs.employeesCount}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 focus:border-orcsys-500 focus:ring-2 focus:ring-orcsys-100 outline-none transition-all"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Taux horaire moyen (€)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                      <DollarSign className="w-5 h-5 text-slate-400" />
                    </div>
                    <input
                      type="number"
                      name="hourlyRate"
                      value={inputs.hourlyRate}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 focus:border-orcsys-500 focus:ring-2 focus:ring-orcsys-100 outline-none transition-all"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Heures par semaine sur ces tâches
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                      <Clock className="w-5 h-5 text-slate-400" />
                    </div>
                    <input
                      type="number"
                      name="hoursPerWeek"
                      value={inputs.hoursPerWeek}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 focus:border-orcsys-500 focus:ring-2 focus:ring-orcsys-100 outline-none transition-all"
                      min="0"
                      max="168"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Semaines travaillées par an
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                      <Calculator className="w-5 h-5 text-slate-400" />
                    </div>
                    <input
                      type="number"
                      name="weeksPerYear"
                      value={inputs.weeksPerYear}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-3 rounded-lg border border-slate-200 focus:border-orcsys-500 focus:ring-2 focus:ring-orcsys-100 outline-none transition-all"
                      min="1"
                      max="52"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Results */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="lg:sticky lg:top-8"
            >
              <div className="bg-slate-900 rounded-xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">
                  Économies potentielles
                </h3>

                <div className="space-y-6 mb-8">
                  <div>
                    <p className="text-slate-400 mb-1">Économies annuelles</p>
                    <p className="text-3xl font-bold text-orcsys-400">
                      {savings.annual.toLocaleString()}€
                    </p>
                  </div>

                  <div>
                    <p className="text-slate-400 mb-1">Économies sur 5 ans</p>
                    <p className="text-3xl font-bold text-orcsys-400">
                      {savings.fiveYear.toLocaleString()}€
                    </p>
                  </div>

                  <div>
                    <p className="text-slate-400 mb-1">Heures récupérées par an</p>
                    <p className="text-3xl font-bold text-orcsys-400">
                      {savings.timeRecovered.toLocaleString()}h
                    </p>
                  </div>
                </div>

                <Button.Link
                  href="#book"
                  className="w-full justify-center bg-white text-slate-900 hover:bg-white/90 group"
                >
                  <span className="flex items-center">
                    Discuter de votre projet
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Button.Link>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator; 